import { useState } from 'react'
import { getUnixTime } from 'date-fns'
import Dialog from '@material-ui/core/Dialog'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import { saveService } from '../../../services/services'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import Alert, { ActionsOptType } from '../../../components/Alert'
import { IInputValue, ITemplate } from '../../../types/templates'
import DialogContentText from '@material-ui/core/DialogContentText'
import { SERVICE_STATUS, SERVICE_TYPE } from '../../../types/services'

// Define IDialogParams interface
interface IDialogParams {
  open: boolean
  template: ITemplate
  setOpen: (newState: boolean) => void
  inputValues: IInputValue[]
  onServiceCreated: (newService: any) => void
}

/**
 * Custom `SaveServiceDialog` component declaration.
 * This component is used to save the creation of the document with a custom message and functionalities
 * @param {Object} template
 * @param {Boolean} open
 * @param {Function} setOpen
 * @param {Array} inputValues
 * @param {Function} onServiceCreated
 * @return SaveServiceDialog
*/
const SaveServiceDialog = ({
  template,
  open,
  setOpen,
  inputValues,
  onServiceCreated
}: IDialogParams): JSX.Element => {
  const [name, setName] = useState<string>('')
  const [alertOptions, setAlertOptions] = useState<ActionsOptType>({
    message: null,
    type: null,
    open: false,
  })

  /**
   * This method is used to capture text to name the service
   * @Return {void}
  */
  const onChangeText = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string
    setName(value)
  }

  /**
   * This method is used to close dialog
   * @Return {void}
  */
  const handleClose = () => {
    setOpen(false)
  }

  /**
   * This method is used to save service data
   * @return {Void}
  */
  const saveNewService = async () => {
    const { data, error } = await saveService({
      service_type_id: SERVICE_TYPE.TEMPLATE,
      service_status_id:
        template.price === 0 ? SERVICE_STATUS.PAID : SERVICE_STATUS.PAYMENT_ON_HOLD,
      service_data: {
        name: name ? name : `${template.name}${getUnixTime(new Date())}`,
        inputValues,
        template_id: template.id,
        first_download: false,
      },
      templates_id: template.id,
    })
    if (data) {
      setAlertOptions({
        message:
          'Se ha creado tu solicitud, revisala en la opción " Mis solicitudes " del menú y realiza el pago para descargar el documento',
        open: true,
        type: 'info',
      })
      setOpen(false)
      onServiceCreated(data.data);
    }
    if (error) setAlertOptions({ message: error, open: true, type: 'error' })
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <Alert alertOptions={alertOptions} setAlertOptions={setAlertOptions} />
      <DialogContent>
        <DialogContentText style={{whiteSpace: 'pre-line'}}>
          {(template.price === 0
            ? '\n NOTA: Los documentos gratuitos no se podrán modificar, presiona el botón guardar para continuar con la creación de tu documento. \n\n'
            : '') +
            ' \n Ingresa el nombre para tu solicitud, la cual se guardará en la opción "Mis solicitudes" del menú ubicado en la parte superior izquierda de tu pantalla.'}
        </DialogContentText>
        <TextField
          autoFocus
          id="name"
          label="Nombre de tu solicitud"
          value={name}
          onChange={onChangeText}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} style={{ color: 'red' }}>
          CANCELAR
        </Button>
        <Button onClick={saveNewService} color="primary">
          GUARDAR
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SaveServiceDialog